import React from 'react';
import RefLink from './RefLink';
import { FLOW_LABELS } from '../workflow/constants';
import { useAuth } from '../../contexts/AuthContext';
import { submitKeysOf, reviewKeyOf } from '../workflow/useWorkflowPerms';

// 「這筆資料是哪張申請單促成的」——單號與來源標記的**唯一一份對照**
// （History 上的 `workflow:cwcc · CWCC-94` 要能點過去看那張單，
// 其他引用 workflow 建立的資料也一樣）。
//
// 為什麼只有一份：這個對照若抄散在各頁（inline regex、只認部分前綴），
// 同一個單號就會在 A 頁能點、B 頁不能點，而新流程上線時沒有人記得回去補
// 每一段 regex——漏掉是必然的。**勿另抄對照，一律引用這裡。**
//
// 對照表以 repository 產生 `request_no` 的地方為準（`UPDATE … SET request_no =
// 'XXX-' || id`），路由以 App.js 的 `/workflow/*` 為準。**新增一條流程時這裡要
// 一起補**，否則它的單號在全站都只會是純文字。
export const WORKFLOW_REF_ROUTES = {
  NC: '/workflow/client',      // wfClientRepository
  CAR: '/workflow/car',
  ENR: '/workflow/engreg',     // wfEngregRepository（不是 ENG——那是已退場的 Setup）
  QCR: '/workflow/qc',
  QCA: '/workflow/qca',
  QCAD: '/workflow/qcad',
  CWCC: '/workflow/cwcc',
  ESC: '/workflow/esc',
  SCR: '/workflow/screening', // wfScreeningRepository
  EAR: '/workflow/ear',
  FS: '/workflow/fs',
  FSA: '/workflow/fsa',
  QC35: '/workflow/qc35',
  QC35A: '/workflow/qc35a',
  CPD: '/workflow/cpd',
  PQ: '/workflow/pq',
  ABS: '/workflow/absence',
  DIS: '/workflow/dispute',
};

/**
 * 單號（`CWCC-94`）→ 詳情路由，認不得就回 null。
 *
 * 認不得**一定要回 null 而不是猜一個路徑**：`ENG-3` 這種已退場流程（舊 Setup）
 * 的單號在歷程裡還留著，但那條流程與它的表已不存在——連過去只會是一個死頁面，
 * 不如讓它維持純文字（歷程本身已經讀得懂）。
 */
export const workflowRefHref = (ref) => {
  const m = /^([A-Z0-9]+)-(\d+)$/.exec(String(ref || '').trim().toUpperCase());
  if (!m) return null;
  const base = WORKFLOW_REF_ROUTES[m[1]];
  return base ? `${base}/${m[2]}` : null;
};

// 歷程的 `source` 欄位用的流程代碼，對到 FLOW_LABELS 的鍵。多數同名，這裡只列
// 對不上的：qcr 是 QC6/QC7 申請單、nc 是 New Client。
const SOURCE_FLOW_ALIAS = { qcr: 'qc', nc: 'client' };

/**
 * 歷程來源 → 人看得懂的名字。
 *
 *   'workflow:cwcc' → 'CaseWare & Charge Code'
 *   'manual'        → null（呼叫端自己決定要寫「Manual edit」還是別的）
 *
 * 對不上的流程代碼回 null，讓呼叫端原樣印出來——`workflow:xyz` 醜但誠實，比
 * 悄悄標成別的名字好。
 */
export const workflowSourceLabel = (source) => {
  const m = /^workflow:([a-z0-9]+)$/i.exec(String(source || '').trim());
  if (!m) return null;
  const key = m[1].toLowerCase();
  return FLOW_LABELS[SOURCE_FLOW_ALIAS[key] || key] || null;
};

// 詳情頁有鑰匙門檻的流程——與 App.js 的路由閘同一組鑰匙（任一把即可進）。
// 登入即可進的流程（自選審核的 NC／CAR／ENR／EAR／QCR／QCA／QCAD／FS／FSA／
// QC35／QC35A 與 CWCC）不列。
const KEYED_REF_PERMS = {
  ESC: [...submitKeysOf('esc'), reviewKeyOf('esc')],
  SCR: [...submitKeysOf('screening'), reviewKeyOf('screening'), ...submitKeysOf('engreg')],
  CPD: ['ld:submit', 'ld:cpd:review'],
  PQ: ['ld:submit', 'ld:pq:review'],
  ABS: ['ld:submit', 'ld:absence:review'],
  DIS: ['ld:submit', 'ld:absence:review'],
};

/**
 * 單號本身：連得過去就是連結，連不過去就是同樣大小的純文字（版面不跳動）。
 * 「連得過去」也看權限：該流程的詳情頁要鑰匙、而呼叫者一把都沒有時印純文字，
 * 不給一個點進去就 Access denied 的連結（例：只持新建提交鑰匙的同仁看得到
 * 案上的 EAR／ESC 歷程，卻開不了那張單）。
 * 連結走 `RefLink`——跨單據參考一律另開新頁。
 *
 * `className` 給呼叫端控制字級/顏色以外的排版；連結色一律藍，與全站「可點的
 * 引用」同一個訊號。
 */
const WorkflowRefLink = ({ refNo, className = 'text-xs' }) => {
  const { hasPermission } = useAuth();
  if (!refNo) return null;
  const prefix = String(refNo).trim().toUpperCase().split('-')[0];
  const perms = KEYED_REF_PERMS[prefix];
  const href = perms && !perms.some(hasPermission) ? null : workflowRefHref(refNo);
  return href ? (
    <RefLink
      to={href}
      className={`${className} text-blue-600 hover:text-blue-800 hover:underline`}
      title={`Open ${refNo} in a new tab`}
    >
      {refNo}
    </RefLink>
  ) : (
    <span className={`${className} text-slate-500`}>{refNo}</span>
  );
};

export default WorkflowRefLink;
